import { Component, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import readXlsxFile from 'read-excel-file/browser';
import writeXlsxFile from 'write-excel-file/browser';
import { ExternalQualityControlService } from '../services/external-quality-control.service';

@Component({
  selector: 'app-external-quality-control-upload',
  imports: [CommonModule],
  templateUrl: './external-quality-control-upload.html',
  styleUrl: './external-quality-control-upload.css',
})
export class ExternalQualityControlUpload {
  excelFilePath = signal<string>('');
  errorMessage = signal<string>('');
  successMessage = signal<string>('');
  isSending = signal<boolean>(false);
  isValidExcel = signal<boolean>(false);

  readonly REQUIRED_HEADERS = [
    'Nexpediente',
    'FechaValidacion (*)',
    'Idufir (*)',
    'RefCatastral (*)',
    'SociedadTasacion',
    'ClienteNombre',
    'ClienteNif'
  ];

  private selectedExcelFile: File | null = null;

  constructor(private router: Router, private externalQualityControlService: ExternalQualityControlService) {}

  goBack() {
    this.router.navigate(['/home']);
  }

  selectExcelFile() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '.xlsx,.xls');

    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        this.selectedExcelFile = file;
        this.excelFilePath.set(file.name);
        this.errorMessage.set('');
        this.successMessage.set('');
        this.isValidExcel.set(false);
        await this.validateExcelHeaders(file);
      }
    };

    input.click();
  }

  async validateExcelHeaders(file: File): Promise<void> {
    try {
      const rows = await readXlsxFile(file);
      if (rows.length === 0) {
        this.errorMessage.set('El Excel no tiene el formato correcto');
        return;
      }
      const headers = rows[0].map(cell => (cell !== null && cell !== undefined ? String(cell) : ''));
      const headersMatch =
        headers.length === this.REQUIRED_HEADERS.length &&
        headers.every((h, i) => h === this.REQUIRED_HEADERS[i]);
      if (!headersMatch) {
        this.errorMessage.set('El Excel no tiene el formato correcto. Las columnas deben ser: ' + this.REQUIRED_HEADERS.join(', '));
      } else {
        this.isValidExcel.set(true);
      }
    } catch {
      this.errorMessage.set('El Excel no tiene el formato correcto');
    }
  }

  canSend(): boolean {
    return !!this.selectedExcelFile && this.isValidExcel() && !this.isSending();
  }

  async downloadExampleExcel(): Promise<void> {
    const EXAMPLE_ROW_1 = [
      'T2160833', '09/02/2026', '190140010041170', '3274301VK8937S0014ZU',
      '', 'BBVA', 'A48265169'
    ];
    const EXAMPLE_ROW_2 = [
      'T2166012', '09/02/2026', '250010015821150', '8093420CG1289S0015DF',
      '', 'BBVA', 'A48265169'
    ];
    const data = [this.REQUIRED_HEADERS, EXAMPLE_ROW_1, EXAMPLE_ROW_2];
    await writeXlsxFile(data, { fileName: 'ejemplo_controles_calidad_externos.xlsx' });
  }

  async sendRequest() {
    if (!this.canSend()) {
      return;
    }

    this.isSending.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    try {
      await firstValueFrom(
        this.externalQualityControlService.createExternalQualityControls(this.selectedExcelFile!)
      );
      this.successMessage.set('Carga masiva de controles de calidad externos enviada correctamente');
    } catch (error: any) {
      const message = error.error?.message || error.message || 'Error desconocido al enviar la solicitud';
      this.errorMessage.set(`Error en el envío: ${message}`);
    } finally {
      this.isSending.set(false);
    }
  }
}
